import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { IAuthService, AuthUser } from '@erp/data-access/interfaces';
import { useNativeBridge } from '@/lib/native-context';
import { writeBootHint } from '@/lib/boot-hint';
import { queryClient } from '@/lib/query-client';
import { removePersistedQueryCache } from '@/lib/query-cache-storage';
import {
  clearPlatformAdminTenantViewSession,
  hasActivePlatformAdminTenantViewAccess,
  hasPlatformAdminTenantHandoffIntent,
} from '@/lib/admin-tenant-view';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  /** Returns true when the user is auto-logged-in after signup, false
   *  when an email confirmation is required. The caller decides what to
   *  do next (route to /welcome or show a "check your inbox" message). */
  signup: (email: string, password: string) => Promise<boolean>;
  /** Sends a password reset email. The redirect URL must be in the
   *  Supabase auth `uri_allow_list` or it falls back to `site_url`. */
  requestPasswordReset: (email: string, redirectTo?: string) => Promise<void>;
  /** Sets a new password on the currently authenticated session. Used
   *  from /auth/update-password after the user clicks the email link. */
  updatePassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  /** True when the auth client just detected a recovery token in the URL
   *  and exchanged it for a session. Components can read this to show
   *  the password-reset UI. Cleared once a non-recovery state change
   *  fires (e.g. after the new password is set). */
  isPasswordRecovery: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({
  children,
  authService,
}: {
  children: ReactNode;
  authService: IAuthService;
}) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const bridge = useNativeBridge();

  useEffect(() => {
    let alive = true;

    withAuthBootTimeout(authService.getCurrentUser())
      .then((u) => {
        if (!alive) return;
        setUser(u);
        setIsLoading(false);
        // Boot hint: if no user, mark logged_out so next launch goes
        // straight to /login. If user exists but tenant unknown, the
        // tenant query will refine it on next mount.
        if (!u) void writeBootHint(bridge.preferences, { kind: 'logged_out' });
      })
      .catch((err) => {
        if (!alive) return;
        console.warn('[auth] initial session read failed, continuing logged out', err);
        if (hasActivePlatformAdminTenantViewAccess() || hasPlatformAdminTenantHandoffIntent()) {
          clearPlatformAdminTenantViewSession();
        }
        setUser(null);
        setIsLoading(false);
        void writeBootHint(bridge.preferences, { kind: 'logged_out' });
      });

    const unsubscribe = authService.onAuthStateChange((u) => {
      setUser(u);
      setIsLoading(false);
      if (!u) void writeBootHint(bridge.preferences, { kind: 'logged_out' });
    });

    // Separate listener for PASSWORD_RECOVERY: when the user clicks the
    // email link, Supabase auto-detects the recovery token in the URL,
    // creates a session, and emits this event. We surface a flag so
    // route guards can redirect to /auth/update-password instead of
    // letting the user roam the app with a half-baked recovery session.
    const unsubRecovery = authService.onPasswordRecovery(() => {
      setIsPasswordRecovery(true);
    });

    return () => {
      alive = false;
      unsubscribe();
      unsubRecovery();
    };
  }, [authService, bridge.preferences]);

  const login = async (email: string, password: string) => {
    // SECURITY: a fresh authentication must never inherit a prior
    // platform-admin "view as tenant" impersonation context. That context
    // (target tenant id + the admin's access token) lives in this tab's
    // sessionStorage and is replayed as global Supabase headers, so a stale
    // tenant-view would otherwise pin the new session to the admin's
    // last-viewed tenant — and run it with admin privileges.
    resetPlatformAdminTenantViewContext();
    const session = await authService.login({ email, password });
    await clearSessionScopedQueryCache();
    setUser(session.user);
    setIsLoading(false);
    setIsPasswordRecovery(false);
  };

  const signup = async (email: string, password: string) => {
    // Same boundary reset as login(): a brand-new account must not inherit a
    // leaked admin tenant-view context (which would otherwise run onboarding
    // — including create_tenant — under the admin's replayed identity).
    resetPlatformAdminTenantViewContext();
    const session = await authService.signup({ email, password });
    await clearSessionScopedQueryCache();
    if (session) {
      setUser(session.user);
      setIsLoading(false);
      setIsPasswordRecovery(false);
      return true;
    }
    return false;
  };

  const requestPasswordReset = async (email: string, redirectTo?: string) => {
    await authService.requestPasswordReset(email, redirectTo);
  };

  const updatePassword = async (newPassword: string) => {
    await authService.updatePassword(newPassword);
    // Clear the recovery flag after the password is successfully set so
    // subsequent navigation isn't bounced back to /auth/update-password.
    setIsPasswordRecovery(false);
  };

  const logout = async () => {
    // Logout is best-effort: even if the network revoke fails (offline,
    // expired token, CSP block), we always wipe the local session so the
    // user actually leaves the app. The next signin will re-establish a
    // fresh session anyway.
    try {
      await authService.logout();
    } catch (err) {
      console.warn('[auth] logout: server signOut failed, clearing local anyway', err);
    }
    if (hasActivePlatformAdminTenantViewAccess() || hasPlatformAdminTenantHandoffIntent()) {
      clearPlatformAdminTenantViewSession();
    }
    await clearSessionScopedQueryCache();
    setUser(null);
    setIsLoading(false);
    setIsPasswordRecovery(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        signup,
        requestPasswordReset,
        updatePassword,
        logout,
        isPasswordRecovery,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

// Tears down any platform-admin "view as tenant" impersonation context
// stored in this tab. Called at every authentication boundary (login,
// signup, logout) so an elevated cross-tenant view can never bleed into a
// normal user session and silently pin it to the admin's last-viewed tenant.
function resetPlatformAdminTenantViewContext() {
  if (hasActivePlatformAdminTenantViewAccess() || hasPlatformAdminTenantHandoffIntent()) {
    clearPlatformAdminTenantViewSession();
  }
}

async function clearSessionScopedQueryCache() {
  queryClient.clear();
  await removePersistedQueryCache().catch(() => undefined);
}

function withAuthBootTimeout<T>(promise: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('auth boot timeout'));
    }, 5000);

    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}
